/**
 * AiStreamingDialog (Iter30 P3).
 *
 * Dialog che apre lo stream SSE su `/api/ai-assistant/mitigations/stream` per
 * una valutazione di rischio, mostra i token in tempo reale e il piano
 * strutturato finale con riferimenti normativi (RAG D.Lgs. 81/2008).
 *
 * Uso:
 *   <AiStreamingDialog
 *     open={...} onOpenChange={...}
 *     companyId={cid}
 *     assessment={riskAssessmentRow}
 *   />
 */
import { useEffect, useRef, useState } from "react";
import { Sparkles, ScrollText, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  alta: "bg-red-100 text-red-700 border-red-200",
  media: "bg-amber-100 text-amber-800 border-amber-200",
  bassa: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function AiStreamingDialog({ open, onOpenChange, companyId, assessment }) {
  const [streaming, setStreaming] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [meta, setMeta] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Pulisci stato al close
      if (abortRef.current) abortRef.current.abort();
      setStreaming(false);
      setTranscript("");
      setMeta(null);
      setPlan(null);
      setError("");
    }
  }, [open]);

  const start = async () => {
    if (!assessment) return;
    setError("");
    setTranscript("");
    setMeta(null);
    setPlan(null);
    setStreaming(true);

    const token = localStorage.getItem("dvr_token");
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${BACKEND_URL}/api/ai-assistant/mitigations/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          risk_assessment_id: assessment.id,
          hazard: assessment.hazard_description || assessment.risk_name || "Rischio rilevato",
          risk_level: assessment.risk_level || "moderato",
          probability: Number(assessment.probability) || 2,
          damage: Number(assessment.damage) || 2,
          ateco_code: assessment.ateco_code || "",
          sector: assessment.sector || "",
          current_controls: (assessment.existing_prevention_measures || []).join("\n"),
          additional_context: assessment.notes || "",
          language: "it",
        }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }

      // SSE parser: legge il body come stream di chunk e identifica gli eventi
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Split by blank line (SSE record separator)
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const record = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const lines = record.split("\n");
          let eventName = "message";
          let dataLine = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataLine += ln.slice(5).trim();
          }
          if (!dataLine) continue;
          try {
            const payload = JSON.parse(dataLine);
            if (eventName === "meta") setMeta(payload);
            else if (eventName === "token") setTranscript((s) => s + (payload.text || ""));
            else if (eventName === "done") setPlan(payload.plan);
            else if (eventName === "saved") toast.success(`Piano AI salvato (id ${payload.id?.slice(0, 8)}…)`);
            else if (eventName === "error") setError(payload.detail || "Errore sconosciuto");
          } catch (_) {
            // ignore malformed line
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") setError(err.message);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] max-w-4xl overflow-y-auto"
        data-testid="ai-streaming-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-900" />
            Piano mitigazione AI (Claude Sonnet 4.5 + RAG D.Lgs. 81/2008)
          </DialogTitle>
          <DialogDescription>
            Generazione live di un piano di mitigazione strutturato per il
            rischio selezionato. La risposta usa il corpus normativo D.Lgs.
            81/2008 indicizzato per fornire riferimenti puntuali.
          </DialogDescription>
        </DialogHeader>

        {assessment && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
              Rischio selezionato
            </p>
            <p className="mt-2 text-lg font-bold text-slate-950">
              {assessment.risk_name}{" "}
              <span className="text-sm font-normal text-slate-500">
                · {assessment.risk_code}
              </span>
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Livello: <strong>{assessment.risk_level}</strong> · P×D ={" "}
              {assessment.probability}×{assessment.damage} ={" "}
              {assessment.initial_risk_score}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            data-testid="ai-stream-start-button"
            className="bg-blue-900 text-white hover:bg-blue-800"
            disabled={streaming}
            onClick={start}
          >
            {streaming ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generazione in corso…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" /> Genera piano AI live
              </>
            )}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="ai-stream-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Errore AI</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {meta?.rag_refs?.length > 0 && (
          <div data-testid="ai-stream-rag-refs">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
              <ScrollText className="mr-1 inline h-3 w-3" />
              Riferimenti normativi recuperati (RAG)
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {meta.rag_refs.map((r, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className="border-blue-200 bg-blue-50 text-blue-900"
                  title={r.title}
                >
                  {r.ref} · score {r.score}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {transcript && (
          <div
            className="rounded-md border border-slate-200 bg-slate-950 p-4 font-mono text-xs text-emerald-300"
            data-testid="ai-stream-transcript"
            style={{ minHeight: 120, maxHeight: 220, overflowY: "auto" }}
          >
            {transcript}
            {streaming && <span className="animate-pulse">▋</span>}
          </div>
        )}

        {plan && (
          <div className="space-y-3" data-testid="ai-stream-plan">
            <Alert className="border-emerald-200 bg-emerald-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" />
              <AlertTitle>Piano strutturato pronto</AlertTitle>
              <AlertDescription>
                Rischio residuo stimato:{" "}
                <strong>{plan.residual_risk_after || "—"}</strong>. Il piano è
                stato salvato e collegato alla valutazione.
              </AlertDescription>
            </Alert>
            {plan.executive_summary && (
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
                  Executive summary
                </p>
                <p className="mt-2 text-sm text-slate-700">{plan.executive_summary}</p>
              </div>
            )}
            {Array.isArray(plan.actions) && plan.actions.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      <th className="p-3">Azione</th>
                      <th className="p-3">Priorità</th>
                      <th className="p-3">Riferimento</th>
                      <th className="p-3">Tempi</th>
                      <th className="p-3">Responsabile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.actions.map((a, i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        <td className="p-3">
                          <p className="font-semibold text-slate-950">{a.title}</p>
                          <p className="mt-1 text-xs text-slate-500">{a.description}</p>
                          <p className="mt-1 text-xs text-slate-400">
                            KPI: {a.kpi}
                          </p>
                        </td>
                        <td className="p-3">
                          <Badge
                            className={PRIORITY_COLORS[a.priority] || "bg-slate-100"}
                          >
                            {a.priority}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-blue-900">
                          {a.legal_reference}
                        </td>
                        <td className="p-3 text-xs">{a.estimated_days}gg</td>
                        <td className="p-3 text-xs">{a.responsible_role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {plan.notes && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Avvertenze</AlertTitle>
                <AlertDescription>{plan.notes}</AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
